import { useCallback } from "react";

import { LearningWalkthroughOutcome } from "~/gql/graphqlBase.generated";
import useLearningMutations from "~/hooks/mutations/useLearningMutations";
import { useMyWalkthroughStateQuery } from "~/services/endpoints/apiEndpoints";
import { listWalkthroughs } from "~/walkthroughs/registry";

const WALKTHROUGH_IDS = listWalkthroughs().map((walkthrough) => walkthrough.id);

export interface UseWalkthroughStateReturn {
	isLoaded: boolean;
	isLoading: boolean;
	markDismissed: (tourId: string, successMessage?: string) => Promise<boolean>;
	markOffered: (tourId: string) => Promise<boolean>;
	offered: (tourId: string) => boolean;
}

const useWalkthroughState = (): UseWalkthroughStateReturn => {
	const { data, isLoading } = useMyWalkthroughStateQuery({ walkthroughIds: WALKTHROUGH_IDS });
	const { recordWalkthroughOutcome } = useLearningMutations();

	const offered = useCallback(
		(tourId: string): boolean =>
			Boolean(data?.myWalkthroughState.find((entry) => entry.walkthroughId === tourId)?.offeredAt),
		[data]
	);

	const markOffered = useCallback(
		(tourId: string): Promise<boolean> =>
			recordWalkthroughOutcome({ outcome: LearningWalkthroughOutcome.Offered, walkthroughId: tourId }),
		[recordWalkthroughOutcome]
	);

	const markDismissed = useCallback(
		(tourId: string, successMessage?: string): Promise<boolean> =>
			recordWalkthroughOutcome(
				{ outcome: LearningWalkthroughOutcome.Dismissed, walkthroughId: tourId },
				successMessage
			),
		[recordWalkthroughOutcome]
	);

	return { isLoaded: data !== undefined, isLoading, markDismissed, markOffered, offered };
};

export default useWalkthroughState;
